import { useCallback } from 'react';
import { ACCESS } from './access.config';
import useAuth from '../hooks/useAuth';

/**
 * @param {string | null | undefined} accessKey - key in ACCESS; empty = allow (no rule)
 * @param {string[]} [userRoles=[]]
 * @param {{ isAsync?: boolean }} [context]
 */
export function hasAccess(accessKey, userRoles = [], context = {}) {
  if (accessKey == null || accessKey === '') return true;
  const rule = ACCESS[accessKey];
  if (rule === undefined) {
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.warn(`[hasAccess] Unknown access key: "${accessKey}"`);
    }
    return false;
  }
  if (rule.requiresAsync && !context.isAsync) return false;
  const allowed = rule.roles;
  if (!allowed || allowed.length === 0) return true;
  return userRoles.some((r) => allowed.includes(r));
}

/** RequireRoles-style check for arbitrary role lists (outside ACCESS). */
export function userHasAnyRole(userRoles = [], allowedRoles = []) {
  if (!Array.isArray(userRoles) || !Array.isArray(allowedRoles)) return false;
  return userRoles.some((r) => allowedRoles.includes(r));
}

/**
 * Query params for GET /admin/chats so list + waiting_response_counts omit types the user cannot see.
 * Derived from ACCESS (coachingChats, supportChats).
 */
export function getChatListExcludeParams(userRoles = []) {
  const excludeTypes = [];
  if (!hasAccess('coachingChats', userRoles)) excludeTypes.push('coaching');
  if (!hasAccess('supportChats', userRoles)) excludeTypes.push('support');
  if (!hasAccess('iopChats', userRoles)) excludeTypes.push('iop');
  if (excludeTypes.length === 0) return {};
  return { exclude_types: excludeTypes.join(',') };
}

/**
 * Returns (accessKey) => boolean for the current user (roles + is_async).
 */
export function useHasAccess() {
  const { user } = useAuth();
  const roles = user?.roles ?? [];
  const isAsync = user?.is_async;

  return useCallback(
    (accessKey) => hasAccess(accessKey, roles, { isAsync }),
    [roles, isAsync]
  );
}
